/**
 * LeadScoreBar — the COMPULSORY 0-10 "how good is this lead?" bar on the
 * disposition form. Shared by the progressive dialler pane
 * (menu-items/Dashboard/DialerCallPane.js) and the click-to-call popup
 * (ui-component/SoftphoneCall.js) so the two cannot drift — they already
 * carry independent copies of the outcome taxonomy, which is exactly how
 * they ended up out of step before.
 *
 * WHY IT EXISTS (Shailendra 2026-10-20)
 * The outcome labels do not predict buying. On the fully-matured Sept 1-18
 * retail cohort (37,134 leads): HOT bought at 0.68%, WARM at 0.41%, while
 * SCOPE/COLD/DEAD — 88.8% of the cohort — bought at 0.11%/0.21%/0.03%. So the
 * five labels the whole queue-priority system runs on carry almost no signal.
 * A continuous score gives a ranking signal on 100% of calls instead of the
 * ~1.5% that order, and becomes the ground truth the Bolna voice qualifier
 * tunes its hand-over threshold against.
 *
 * COMPULSORY on purpose: an optional bar simply does not get filled, and an
 * empty column teaches us nothing. Rubber-stamping is caught by the
 * contradiction guard (below + server-side) and by the flatScorer / drift
 * checks in leadScoreReport.js — not by leaving the field blank.
 */
import React from 'react';
import { Box, Typography, Slider } from '@material-ui/core';

// Where the bar STARTS when an outcome is picked. The agent can move it; we
// keep both numbers server-side so the drift is measurable.
// SCOPE is absent deliberately — "no real conversation happened" means there
// is nothing to judge, and scoring it would have put 13,304 meaningless rows
// into the Sept cohort alone.
export const LEAD_SCORE_AUTO = { WON: 10, HOT: 8, WARM: 6, COLD: 3, DEAD: 1 };

// The outcome and the score have to tell the same story. "HOT but scored 0"
// is a mis-click or a rubber stamp, and either way it corrupts the one signal
// we are building. Bands are deliberately WIDE — an agent may disagree with
// our seed by a lot, they just cannot invert it. Mirrored exactly in
// recordCallOutcome() (routes.js) so the server rejects it too; this copy only
// exists so the agent sees the problem before they hit submit.
export const SCORE_BAND = {
  WON:  [7, 10],   // an order was placed — it cannot be a bad lead
  HOT:  [5, 10],
  WARM: [3, 10],
  COLD: [0, 7],
  DEAD: [0, 4],    // if it deserves >4, it is not Not-Interested
};

export const scoreNeeded = (outcome) => !!outcome && outcome !== 'SCOPE';

/** null when fine, else the message to show. Used for the submit guard too. */
export function scoreContradiction(outcome, score) {
  const band = SCORE_BAND[outcome];
  if (!band || score === null || score === undefined) return null;
  if (score >= band[0] && score <= band[1]) return null;
  return score < band[0]
    ? `The slider doesn't match the status. "${outcome}" needs ${band[0]} or more — either raise the score, or change the status to a lower one.`
    : `The slider doesn't match the status. "${outcome}" tops out at ${band[1]} — either lower the score, or change the status to a higher one.`;
}

const bandColor = (v) => (v <= 3 ? '#78909c' : v <= 6 ? '#e65100' : '#1b5e20');
const bandWord  = (v) => (v <= 1 ? 'Waste' : v <= 3 ? 'Weak' : v <= 6 ? 'Maybe' : v <= 8 ? 'Good' : 'Will buy');

/**
 * `value` is null until the agent drags. The slider still RESTS at 0 so the
 * control reads as a real 0-10 bar rather than an empty widget, but null and
 * 0 stay distinct: null = not rated, 0 = "Waste", a judgement the agent is
 * allowed to make. Callers must gate submit on null, never on 0.
 */
export default function LeadScoreBar({ outcome, value, onChange }) {
  // ⚠️ VISIBLE BEFORE AN OUTCOME IS PICKED (Shailendra 2026-10-21).
  // This used to render null until the agent clicked an outcome card, so on a
  // fresh form there was no bar at all — agents reported "it isn't showing"
  // and were right: nothing is on screen to fill in. It now renders from the
  // start, resting at 0 and marked Required, so the agent sees the ask before
  // they disposition rather than after.
  //
  // SCOPE still hides it: "no real conversation happened" means there is
  // nothing to judge, and scoring it would have put 13,304 meaningless rows
  // into the Sept cohort alone.
  if (outcome === 'SCOPE') return null;

  const v = value === null || value === undefined ? null : Number(value);
  const warn = scoreContradiction(outcome, v);
  const color = v === null ? '#c9ced6' : bandColor(v);

  return (
    <Box
      style={{
        marginTop: 6, marginBottom: 10, padding: '10px 12px 4px',
        border: `1.5px solid ${warn ? '#c62828' : (v === null ? '#e65100' : '#e6e9ee')}`,
        borderRadius: 9,
        background: warn ? '#fdecea' : (v === null ? '#fff8f2' : '#fbfcfd'),
      }}
    >
      <Box style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography style={{ fontSize: 12, fontWeight: 800, color: '#2b3038' }}>
          How good is this lead?{' '}
          <span style={{ color: '#c62828' }}>*</span>
        </Typography>
        <Typography style={{ fontSize: 13, fontWeight: 900, color }}>
          {v === null ? '—' : `${v} · ${bandWord(v)}`}
        </Typography>
      </Box>

      <Slider
        value={v === null ? 0 : v}
        onChange={(e, nv) => onChange(Array.isArray(nv) ? nv[0] : nv)}
        step={1}
        min={0}
        max={10}
        marks={[{ value: 0 }, { value: 5 }, { value: 10 }]}
        valueLabelDisplay="auto"
        style={{ color, marginTop: 2, padding: '12px 0 6px' }}
      />

      <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: -6 }}>
        <Typography style={{ fontSize: 10, color: '#8b929c', fontWeight: 600 }}>0 · Waste</Typography>
        <Typography style={{ fontSize: 10, color: '#8b929c', fontWeight: 600 }}>10 · Will buy</Typography>
      </Box>

      {warn && (
        <Typography style={{ fontSize: 11, color: '#c62828', fontWeight: 700, lineHeight: 1.35, paddingBottom: 6 }}>
          {warn}
        </Typography>
      )}
      {!warn && v === null && (
        <Typography style={{ fontSize: 11, color: '#e65100', fontWeight: 700, paddingBottom: 6 }}>
          Required — drag the bar to rate this lead. You cannot submit until you do.
        </Typography>
      )}
    </Box>
  );
}
